import React, { useMemo, useState } from "react";
import { create, all } from "mathjs";
import { Play, Lightning } from "@phosphor-icons/react";

const math = create(all, {});
// Block runtime-extension entry points; KEEP parse/evaluate intact (parser depends on them).
// Sandboxing is enforced by parser.set() — only declared scope variables resolve.
math.import(
  {
    import: function () {
      throw new Error("disabled");
    },
    createUnit: function () {
      throw new Error("disabled");
    },
  },
  { override: true },
);

const formatValue = (v, format) => {
  if (v === null || v === undefined || (typeof v === "number" && Number.isNaN(v))) {
    return "—";
  }
  if (typeof v !== "number") return String(v);
  switch (format) {
    case "currency":
      return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    case "percent":
      return `${(v * 100).toFixed(2)}%`;
    case "integer":
      return Math.round(v).toLocaleString();
    case "number":
    default:
      return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
};

const coerce = (raw, type) => {
  if (type === "number" || type === "currency" || type === "percent" || type === "integer") {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }
  return raw;
};

const evalExpr = (expr, scope) => {
  try {
    const parser = math.parser();
    Object.entries(scope).forEach(([k, v]) => parser.set(k, v));
    return parser.evaluate(expr);
  } catch (e) {
    return null;
  }
};

const MiniAppRunner = ({ spec, compact = false }) => {
  const inputs = spec?.inputs || [];
  const outputs = spec?.outputs || [];

  // Detect whether this is a "new" runnable spec (inputs have keys + outputs have expr)
  const runnable =
    inputs.length > 0 &&
    inputs.every((i) => i.key) &&
    outputs.length > 0 &&
    outputs.every((o) => o.expr);

  const initial = useMemo(() => {
    const obj = {};
    inputs.forEach((i) => {
      obj[i.key || i.label] =
        i.default !== undefined && i.default !== null ? i.default : i.type === "text" ? "" : 0;
    });
    return obj;
  }, [spec]);

  const [values, setValues] = useState(initial);

  if (!runnable) {
    return (
      <div className="border border-dashed border-[#A8977B] bg-[#F7F6F2] p-4 text-sm text-[#5a4d34]" data-testid="not-runnable">
        <Lightning size={14} className="inline mr-2" weight="fill" />
        This spec was generated before the runnable upgrade. Click <strong>Edit → Save & Regenerate Spec</strong> to make it runnable in-app.
      </div>
    );
  }

  const scope = {};
  inputs.forEach((i) => {
    scope[i.key] = coerce(values[i.key], i.type);
  });

  const computed = outputs.map((o) => ({
    ...o,
    value: evalExpr(o.expr, scope),
  }));

  return (
    <div className="space-y-4" data-testid="mini-app-runner">
      <div className={`grid ${compact ? "" : "lg:grid-cols-2"} gap-6`}>
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Play size={14} className="text-[#A8977B]" weight="fill" />
            <p className="label-eyebrow">Run This Mini-App</p>
          </div>
          {inputs.map((inp) => {
            const isNumeric = ["number", "currency", "percent", "integer"].includes(inp.type);
            return (
              <div key={inp.key}>
                <label className="label-eyebrow block mb-2">{inp.label}</label>
                <input
                  data-testid={`run-input-${inp.key}`}
                  type={isNumeric ? "number" : inp.type === "date" ? "date" : "text"}
                  step={inp.type === "currency" || inp.type === "percent" ? "0.01" : "1"}
                  className={`input-clean ${isNumeric ? "font-mono" : ""}`}
                  value={values[inp.key] ?? ""}
                  onChange={(e) => setValues({ ...values, [inp.key]: e.target.value })}
                />
                {inp.hint && <p className="label-eyebrow mt-1 opacity-60">{inp.hint}</p>}
              </div>
            );
          })}
        </div>

        <div className="space-y-3">
          <p className="label-eyebrow mb-1">Results</p>
          {computed.map((o, i) => (
            <div
              key={o.key || i}
              className="border border-[#1A2F24]/12 p-4"
              data-testid={`run-output-${o.key}`}
            >
              <p className="label-eyebrow">{o.label}</p>
              <p
                className={`font-mono mt-2 ${
                  i === 0 ? "text-[#A8977B] text-2xl" : "text-[#1A2F24] text-lg"
                }`}
              >
                {formatValue(o.value, o.format)}
              </p>
              {o.description && (
                <p className="text-xs text-[#606C64] mt-1 italic">{o.description}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MiniAppRunner;
